import { useState } from 'react'
import { Link } from 'react-router-dom'
import { api, errorText } from '../api/client'
import type { Option, Relationship } from '../api/types'
import { titleCase } from '../lib/format'
import { useAsync } from '../lib/useAsync'
import { Icon } from './Icon'
import { Modal } from './Modal'
import { useConfirm } from './Confirm'
import { useToast } from './Toast'

/** The people around a borrower: their realtor, the title officer on the file, a
 *  co-borrower, the partner who sent them. Links are stored once and read from
 *  both sides — the realtor's card shows the borrower as a client. */
export function Relationships({ contactId, name }: { contactId: number; name: string | null }) {
  const [bump, setBump] = useState(0)
  const { data } = useAsync(() => api.relationships(contactId), [contactId, bump])
  const [adding, setAdding] = useState(false)
  const toast = useToast()
  const confirm = useConfirm()
  const links = data?.relationships ?? []

  async function remove(link: Relationship) {
    const ok = await confirm({
      title: `Remove the link to ${link.name ?? 'them'}?`,
      body: link.mirrored
        ? 'It was made from their card, so it disappears from both.'
        : 'It disappears from their card too.',
      confirmLabel: 'Remove link', danger: true,
    })
    if (!ok) return
    try {
      await api.deleteRelationship(link.id)
      setBump((n) => n + 1)
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
    }
  }

  return (
    <section className="card">
      <div className="card-head">
        <h2 className="h2">Relationships</h2>
        {links.length ? <span className="count-pill">{links.length}</span> : null}
        <span className="spacer" />
        <button className="btn sm" onClick={() => setAdding(true)}><Icon name="add" size={15} /> Link</button>
      </div>
      <div className="card-body" style={{ paddingTop: 4 }}>
        {links.length === 0 ? (
          <p className="quiet" style={{ margin: 0, fontSize: 12.5 }}>
            Nobody linked yet — their realtor, a co-borrower, whoever referred them.
          </p>
        ) : links.map((l) => (
          <div key={`${l.id}-${l.mirrored}`} className="relrow">
            <span className="rel-role">{l.label}</span>
            <span style={{ flex: 1, minWidth: 0 }}>
              {l.href ? <Link to={l.href}>{l.name ?? '—'}</Link> : <span>{l.name ?? '—'}</span>}
              <span className="quiet" style={{ display: 'block', fontSize: 11.5 }}>
                {[l.note, l.lead_status ? titleCase(l.lead_status) : null,
                  l.mirrored ? 'linked from their card' : null].filter(Boolean).join(' · ')}
              </span>
            </span>
            <button className="icon-btn-plain" onClick={() => remove(l)}
              title={l.mirrored ? 'Remove this link — it goes from their card too' : 'Remove this link'}
              aria-label={`Remove the link to ${l.name ?? 'them'}`}><Icon name="delete" size={15} /></button>
          </div>
        ))}
      </div>
      {adding ? (
        <LinkModal contactId={contactId} name={name} roles={data?.roles ?? []}
          onClose={() => setAdding(false)} onSaved={() => { setBump((n) => n + 1); setAdding(false) }} />
      ) : null}
    </section>
  )
}

function LinkModal({ contactId, name, roles, onClose, onSaved }: {
  contactId: number
  name: string | null
  roles: Option[]
  onClose: () => void
  onSaved: () => void
}) {
  const [role, setRole] = useState('realtor')
  const [q, setQ] = useState('')
  const [picked, setPicked] = useState<{ contact_id?: number; person_key?: string; name: string } | null>(null)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const toast = useToast()
  const { data } = useAsync(() => (q.trim().length >= 2 ? api.search(q.trim()) : Promise.resolve(null)), [q])
  // Contacts and loan-file people both, because a realtor is often only on a file.
  type Candidate = { contact_id?: number; person_key?: string; name: string; hint: string }
  const results: Candidate[] = [
    ...(data?.leads ?? []).filter((l) => l.id !== contactId).map((l) => ({
      contact_id: l.id, name: l.full_name ?? `#${l.id}`,
      hint: [l.city, titleCase(l.status)].filter(Boolean).join(' · ') || 'Contact',
    })),
    ...(data?.people ?? []).filter((p) => !p.contact_id).map((p) => ({
      person_key: p.person_key, name: p.name ?? p.person_key,
      hint: [p.company, p.roles?.replace(/_/g, ' ')].filter(Boolean).join(' · ') || 'On a loan file',
    })),
  ].slice(0, 8)

  async function save() {
    if (!picked) return
    setBusy(true)
    try {
      await api.addRelationship(contactId, { role, other_contact_id: picked.contact_id, other_person_key: picked.person_key, note: note.trim() || undefined })
      toast(`Linked ${picked.name}`, { tone: 'positive' })
      onSaved()
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
      setBusy(false)
    }
  }

  return (
    <Modal open width={520} title={`Link someone to ${name ?? 'this contact'}`} onClose={onClose}
      footer={<>
        <span className="spacer" />
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn primary" disabled={!picked || busy} onClick={save}>Link them</button>
      </>}>
      <div className="stack">
        <label className="fl">
          <span>They are this contact's…</span>
          <select className="field" value={role} onChange={(e) => setRole(e.target.value)}>
            {roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
        </label>
        <label className="fl">
          <span>Who</span>
          {picked ? (
            <span className="tag" style={{ alignSelf: 'flex-start' }}>{picked.name}
              <button onClick={() => setPicked(null)} aria-label="Pick someone else"><Icon name="close" size={13} /></button>
            </span>
          ) : (
            <input className="field" autoFocus value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search contacts and people on your loan files" />
          )}
        </label>
        {!picked && q.trim().length >= 2 ? (
          <div>
            {results.length === 0 ? <p className="quiet" style={{ margin: 0, fontSize: 12.5 }}>Nobody matches “{q.trim()}”.</p>
              : results.map((r) => (
                <button key={r.contact_id ?? r.person_key} className="taskrow wf-row"
                  onClick={() => { setPicked(r); setQ('') }}>
                  <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                    <span className="task-title" style={{ display: 'block' }}>{r.name}</span>
                    <span className="task-meta">{r.hint}</span>
                  </span>
                  <Icon name="add_link" size={17} style={{ color: 'var(--accent)' }} />
                </button>
              ))}
          </div>
        ) : null}
        <label className="fl">
          <span>Note <span className="hint">— optional</span></span>
          <input className="field" value={note} maxLength={200} placeholder="e.g. Showed them 12 Prior St"
            onChange={(e) => setNote(e.target.value)} />
        </label>
      </div>
    </Modal>
  )
}
